import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Sparkles, ChevronRight, Loader2, Plus, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PromotionsPage() {
  const [rules, setRules] = useState([{ from_class: "", to_class: "", min_average: 10 }]);
  const [academicYear, setAcademicYear] = useState("2025-2026");
  const [yearTo, setYearTo] = useState("2026-2027");
  const [classes, setClasses] = useState([]);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [loadingDetect, setLoadingDetect] = useState(false);
  const [loadingExec, setLoadingExec] = useState(false);
  const [targetClass, setTargetClass] = useState("");

  useEffect(() => {
    api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {});
  }, []);

  const detect = async () => {
    const validRules = rules.filter(r => r.from_class && r.to_class);
    if (validRules.length === 0) return toast.error("Ajoutez au moins une règle");
    setLoadingDetect(true);
    try {
      const { data } = await api.post("/sprint-f/promotions/detect", {
        academic_year: academicYear,
        rules: validRules.map(r => ({ ...r, min_average: parseFloat(r.min_average) || 10 })),
      });
      setResults(data);
      const preSelected = {};
      data.results.filter(r => r.admitted).forEach(r => { preSelected[r.student_id] = r.target_class; });
      setSelected(preSelected);
      toast.success(`${data.admitted_count} élève(s) détecté(s) comme admis sur ${data.total} analysés`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoadingDetect(false); }
  };

  const executePromotion = async (toClass) => {
    const ids = Object.entries(selected).filter(([id, cls]) => cls === toClass).map(([id]) => id);
    if (ids.length === 0) return toast.error("Aucun élève sélectionné pour cette classe");
    setLoadingExec(true);
    try {
      const { data } = await api.post("/sprint-f/promotions/execute", {
        student_ids: ids,
        to_class: toClass,
        academic_year_from: academicYear,
        academic_year_to: yearTo,
      });
      toast.success(`${data.promoted_count} élève(s) promus en ${toClass}`);
      // refresh: remove promoted from list
      setResults({ ...results, results: results.results.filter(r => !ids.includes(r.student_id)) });
      const newSel = { ...selected };
      ids.forEach(id => delete newSel[id]);
      setSelected(newSel);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoadingExec(false); }
  };

  const targetClasses = results ? [...new Set(results.results.filter(r => r.admitted).map(r => r.target_class))] : [];

  return (
    <div className="space-y-6" data-testid="promotions-page">
      <div>
        <div className="chip mb-2"><GraduationCap className="h-3 w-3" /><span>FIN D&apos;ANNÉE — PROMOTIONS</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Promotion de classe automatique</h1>
        <p className="text-slate-500 mt-1">Détectez les élèves admis en classe supérieure selon leur moyenne, puis affectez-les en une action.</p>
      </div>

      {/* Rules editor */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Année scolaire terminée</Label>
            <Input value={academicYear} onChange={e => setAcademicYear(e.target.value)} placeholder="2025-2026" data-testid="academic-year" />
          </div>
          <div>
            <Label>Année scolaire à venir</Label>
            <Input value={yearTo} onChange={e => setYearTo(e.target.value)} placeholder="2026-2027" data-testid="year-to" />
          </div>
        </div>

        <div>
          <Label>Règles de promotion (classe actuelle → classe cible + moyenne min.)</Label>
          <div className="space-y-2 mt-2">
            {rules.map((r, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <Input list="classes-list" className="sm:col-span-5" value={r.from_class} onChange={e => { const nr = [...rules]; nr[i].from_class = e.target.value; setRules(nr); }} placeholder="Ex : Primaire CE2" data-testid={`rule-from-${i}`} />
                <ChevronRight className="hidden sm:block sm:col-span-1 h-5 w-5 text-slate-400 mx-auto mt-2" />
                <Input list="classes-list" className="sm:col-span-4" value={r.to_class} onChange={e => { const nr = [...rules]; nr[i].to_class = e.target.value; setRules(nr); }} placeholder="Ex : Primaire CM1" data-testid={`rule-to-${i}`} />
                <Input className="sm:col-span-1" type="number" step="0.5" value={r.min_average} onChange={e => { const nr = [...rules]; nr[i].min_average = e.target.value; setRules(nr); }} data-testid={`rule-avg-${i}`} />
                <Button variant="ghost" className="sm:col-span-1" size="icon" onClick={() => setRules(rules.filter((_, j) => j !== i))} data-testid={`rule-del-${i}`}><X className="h-4 w-4" /></Button>
              </div>
            ))}
            <datalist id="classes-list">
              {classes.map(c => <option key={c.id || c.name} value={c.name} />)}
            </datalist>
            <Button size="sm" variant="outline" onClick={() => setRules([...rules, { from_class: "", to_class: "", min_average: 10 }])} className="gap-1"><Plus className="h-3 w-3" />Ajouter une règle</Button>
          </div>
        </div>

        <Button onClick={detect} disabled={loadingDetect} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white gap-2" data-testid="btn-detect">
          {loadingDetect ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" />Détecter les admis</>}
        </Button>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
            <div className="font-heading font-bold text-[#1E3A8A]">
              {results.admitted_count} admis / {results.total} élèves analysés
            </div>
            <div className="flex items-center gap-2">
              {targetClasses.map(tc => (
                <Button key={tc} size="sm" onClick={() => executePromotion(tc)} disabled={loadingExec} className="bg-[#166534] hover:bg-[#14532d] text-white gap-1 text-xs" data-testid={`btn-promote-${tc}`}>
                  <CheckCircle2 className="h-3 w-3" />Promouvoir vers {tc}
                </Button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 w-8"></th>
                  <th className="px-4 py-2 text-left">Élève</th>
                  <th className="px-4 py-2 text-left">Classe actuelle</th>
                  <th className="px-4 py-2 text-right">Moyenne</th>
                  <th className="px-4 py-2 text-left">Cible</th>
                  <th className="px-4 py-2 text-center">Statut</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map(r => (
                  <tr key={r.student_id} className="border-t border-slate-100" data-testid={`prom-row-${r.student_id}`}>
                    <td className="px-4 py-2">
                      {r.admitted && (
                        <input type="checkbox"
                          checked={!!selected[r.student_id]}
                          onChange={e => { const s = { ...selected }; if (e.target.checked) s[r.student_id] = r.target_class; else delete s[r.student_id]; setSelected(s); }}
                          data-testid={`sel-${r.student_id}`} />
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">{r.student_name}</td>
                    <td className="px-4 py-2">{r.current_class}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{r.average}/20</td>
                    <td className="px-4 py-2">{r.target_class}</td>
                    <td className="px-4 py-2 text-center">
                      {r.admitted ? <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 font-semibold">✓ Admis</span>
                                   : <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 font-semibold">✗ Non admis</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
